/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment completion progress utilities
 *
 * Mirrors one-reader AssignmentHelperUtils.getProgressSummary / buildCompletionProgressSegments.
 *
 * @file assignmentProgressUtils.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import {
  getCompletionTooltipTextColor,
  getProgressColor
} from '../../common/statusColorUtils';

const COMPLETION_STATUS_CONFIG = [
  {
    key: 'completed',
    countKey: 'completedOnTimeCount',
    percentageKey: 'completedOnTimePercentage',
    type: 'completed',
    defaultLabel: 'Completed on time'
  },
  {
    key: 'pastDueCompleted',
    countKey: 'completedLateCount',
    percentageKey: 'completedLatePercentage',
    type: 'completedPastDue',
    defaultLabel: 'Completed past due'
  },
  {
    key: 'inProgress',
    countKey: 'inProgressCount',
    percentageKey: 'inProgressPercentage',
    type: 'inprogress',
    defaultLabel: 'In progress'
  },
  {
    key: 'notStarted',
    countKey: 'notStartedCount',
    percentageKey: 'notStartedPercentage',
    type: 'notstarted',
    defaultLabel: 'Not attempted'
  }
];

export function buildCompletionProgressSegments(progress = {}, labels = {}) {
  return COMPLETION_STATUS_CONFIG.map(({
    key,
    countKey,
    percentageKey,
    type,
    defaultLabel
  }) => {
    const count = Number(progress[countKey]) || 0;
    const percentage = Number(progress[percentageKey]) || 0;

    return {
      label: labels[key] ?? defaultLabel,
      value: percentage,
      displayValue: `${count} (${percentage}%)`,
      color: getProgressColor(type),
      type
    };
  });
}

export function buildCompletionProgressTooltipSegments(segments = []) {
  return segments.map((segment) => ({
    ...segment,
    value: segment.displayValue ?? segment.value,
    color: segment.tooltipColor ?? getCompletionTooltipTextColor(segment?.type)
  }));
}

export function getProgressSummary(progress = {}, labels = {}) {
  return buildCompletionProgressSegments(progress, labels);
}
